import cx from 'classnames';
import {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {NamedStepsNav} from '~/CreateFabricInvitation/components/NavHeader/components/NamedStepsNav';
import {NamedStepsNavCTAs} from '~/CreateFabricInvitation/components/NavHeader/components/NamedStepsNavCTAs';
import {
  getNavigationSteps,
  navigateToRoute,
  navigateToRoute as navigateToRouteCreateFabric,
} from '~/CreateFabricInvitation/navigation';
import {backToGallery} from '~/CreateFabricInvitation/navigation/utils';
import {validateEventDetails} from '~/CreateFabricInvitation/navigation/validation';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  selectPristineBackground,
  selectPristineEnvelopeDesign,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {isProOrPartyPassUser} from '~/CreateFabricInvitation/utils/experiments';
import {isInAppMode} from '~/common/checkout/utils/app';
import {useNavBottomState} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavBottomState';
import {useValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useValidation';
import {INIT_UNLOCK_STATE, UNLOCK_STEP} from '~/common/fabric/constants';
import {
  createInitialState,
  unlockedStepsReducer,
} from '~/common/fabric/reducers/createFlowNav/reducer';
import {selectIsDetailsPristine} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectPristine, selectSavingStep} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {ProLogoIcon} from '~library/svg/v2/ProLogoIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';

import {premiumColor} from '~libSass/base/_exports.sass';
import './NamedStepsNavWrapper.sass';

export const NamedStepsNavWrapper = ({
  currentRoute,
  isPremium,
  eventId,
  totalSteps,
  handleShowLeaveModal,
  onSave,
  premiumPreview = false,
  store,
  navigateToRouteHook = navigateToRouteCreateFabric,
}) => {
  const {showUpsellModal, handleModals} = useNavBottomState();
  const inAppMode = isInAppMode();
  const isLoading = useSelector(selectSavingStep);
  const isMediumAndUp = useIsMediumAndUp();
  const showProLogo = isProOrPartyPassUser(window?.subscriptionPlanName);
  const {validate: postValidate, ErrorModal} = useValidation();
  const inEditMode = useSelector(eventInEditMode);
  const allStepsArr = [];
  const navigationSteps = getNavigationSteps(inEditMode, store.getState());
  const potentialStepsToShow = ['customize', 'details', 'gifting', 'preview', 'guests'];
  const filteredEventSteps = useMemo(() => {
    if (!navigationSteps) return {};
    const stepsMap = new Map();

    Object.values(navigationSteps)
      .filter((step) => typeof step.id === 'number') // Ensure the step has a valid `id`
      .forEach((step) => {
        stepsMap.set(step.id, step); // Replace any existing step with the same `id`
      });

    return Array.from(stepsMap.values()).reduce((filteredSteps, step) => {
      filteredSteps[step.name] = step; // Use `name` as the key in the final object
      return filteredSteps;
    }, {});
  }, [navigationSteps]);
  Object.values(filteredEventSteps).forEach((step) => allStepsArr.push(step.name)); // Unlock all steps for edit mode
  const stepsToShow = potentialStepsToShow.filter((step) => filteredEventSteps[step]);
  const activeStep = currentRoute.name;
  const hasBeenSaved = !!eventId;
  const isFirstStep = currentRoute.name === 'customize';
  const isCheckoutStep = currentRoute.name === 'review';
  const stepSpecificBackButtonText =
    isFirstStep && !hasBeenSaved ? 'Back to browse' : 'Back to edit';
  const initialState = createInitialState({inEditMode, activeStep, filteredEventSteps});
  const [unlockedSteps, dispatchReducer] = useReducer(unlockedStepsReducer, initialState);
  const isDetailsPristine = useSelector(selectIsDetailsPristine);
  const isDesignPristine = useSelector(selectPristine);
  const isUyoPristine = useSelector(selectPristineEnvelopeDesign);
  const isBackgroundPristine = useSelector(selectPristineBackground);
  const [nextRoute, setNextRoute] = useState('');
  const isPristine = isDesignPristine && isBackgroundPristine && isUyoPristine && isDetailsPristine;

  const setIsLoading = useCallback(
    (loading) => {
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: loading},
      });
    },
    [store]
  );

  const handleUnlockStep = useCallback(
    async (step) => {
      const currentStep = filteredEventSteps[step];
      const updatedSteps = Object.values(filteredEventSteps)
        .filter((s) => s.id <= currentStep.id)
        .map((s) => s.name);

      dispatchReducer({
        type: UNLOCK_STEP,
        payload: updatedSteps,
      });
    },
    [filteredEventSteps]
  );

  const handleReviewNextButtonClick = useCallback(async () => {
    return await validateEventDetails(
      store,
      currentRoute.name === 'preview' ? postValidate : undefined,
      true
    );
  }, [currentRoute.name, store, postValidate]);

  const handleNextClick = useCallback(async () => {
    try {
      const navigating = await navigateToRouteHook(
        currentRoute.nextRoute,
        store,
        false, // Default value
        false, // Default value
        [true] // Force run preLeaveRouteFunction
      );
      if (!navigating) {
        evite.error('Failed to navigate to step ', currentRoute.nextRoute);
        return false;
      }
      return true; // navigation succeeded
    } catch {
      setIsLoading(false);
      return false;
    }
  }, [currentRoute.nextRoute, store, setIsLoading, navigateToRouteHook]);

  const handleBackClick = useCallback(async () => {
    if (isFirstStep && isPristine) {
      window.history.back(); // if this exists/works, then the next line won't run
      return backToGallery(true);
    }
    if (isCheckoutStep) {
      return navigateToRoute('preview', store);
    }
    if (!isPristine) {
      setIsLoading(false);
      return await handleShowLeaveModal();
    }
    return navigateToRoute('back', store);
  }, [isFirstStep, isPristine, handleShowLeaveModal, isCheckoutStep, store, setIsLoading]);

  const renderLoadingPortal = () => {
    const fabricRoot = document.getElementById('fabric-create-invitation-root');
    if (!fabricRoot || !isLoading) return null;
    return createPortal(
      <LoadingOverlay loadingMessage="Loading..." show={isLoading} />,
      fabricRoot
    );
  };

  const preClickHandler = useCallback(
    async (step, directionParam = '') => {
      let direction = directionParam;
      if (step === 'previous') direction = 'back';
      else if (step === 'next') direction = 'next';
      if (['preview', 'guests'].includes(step) && !isMediumAndUp)
        scrollElementIntoViewById('guests-step');

      let goingForward = direction === 'next';
      if (!['back', 'next'].includes(direction)) {
        if (filteredEventSteps[step].id === filteredEventSteps[activeStep].id) return; // Same as current step, no navigation
        goingForward = filteredEventSteps[step].id > filteredEventSteps[activeStep].id;
      }

      let func;
      if (direction === 'back') func = handleBackClick;
      else {
        const isValidationStep = ['preview', 'guests'].includes(currentRoute.name);

        if (isValidationStep && goingForward) {
          const isValid = await handleReviewNextButtonClick?.();
          if (!isValid) return;
        }

        if (premiumPreview) {
          setNextRoute(step);
          return handleModals.toggleUpsellModal();
        }
        setNextRoute('');

        setIsLoading(true);
        func = direction === 'next' ? handleNextClick : () => navigateToRoute(step, store);
      }

      const result = await func(step);

      if (!result) return setIsLoading(false);

      await handleUnlockStep(step);
      setIsLoading(false);
    },
    [
      isMediumAndUp,
      handleBackClick,
      handleNextClick,
      store,
      handleUnlockStep,
      setIsLoading,
      handleReviewNextButtonClick,
      premiumPreview,
      handleModals.toggleUpsellModal,
      currentRoute.name,
      activeStep,
      filteredEventSteps,
    ]
  );

  const modalHandlers = useMemo(
    () => ({
      ...handleModals,
      downgradeAndContinue: () => handleModals.downgradeAndContinue(nextRoute),
      upgradeAndContinue: () => handleModals.upgradeAndContinue(nextRoute),
    }),
    [handleModals, nextRoute]
  );

  useEffect(() => {
    dispatchReducer({
      type: INIT_UNLOCK_STATE,
      payload: {
        inEditMode: inEditMode,
        activeStep: activeStep,
        filteredEventSteps: filteredEventSteps,
      },
    });
  }, [inEditMode, activeStep, filteredEventSteps]);

  useEffect(() => {
    if (['preview', 'guests'].includes(activeStep) && !isMediumAndUp)
      scrollElementIntoViewById('guests-step');
  }, [activeStep, isMediumAndUp]);

  if (currentRoute.showMegaNav) {
    return null;
  }

  return (
    <nav
      className={cx('named-steps-nav', {
        frp: currentRoute.id > totalSteps,
        'named-steps-nav--checkout': isCheckoutStep,
      })}
    >
      {!inAppMode && (
        <div className="named-steps-nav__left">
          {isMediumAndUp || (!isMediumAndUp && isCheckoutStep)
            ? <Button
                variant="secondary"
                size={isMediumAndUp ? 'medium' : 'small'}
                data-qa-id="back-to-browse"
                onClick={() => preClickHandler(currentRoute.previousRoute, 'back')}
              >
                {(isFirstStep && !hasBeenSaved) || isCheckoutStep
                  ? <>
                      {isFirstStep && !hasBeenSaved
                        ? <ChevronIcon direction="left" ratio={0.75} />
                        : null}
                      {stepSpecificBackButtonText}
                    </>
                  : 'Save & exit'}
              </Button>
            : <Button
                data-qa-id="close"
                variant="tertiary"
                size="medium"
                disableProcessing
                onClick={() => preClickHandler(currentRoute.previousRoute, 'back')}
              >
                <CloseLineIcon ratio={0.75} />
              </Button>}
          {isMediumAndUp && (isPremium || showProLogo)
            ? <Typography
                variant="paragraph2"
                color="premiumColor"
                className="named-steps-nav__premium-badge"
              >
                {showProLogo
                  ? <ProLogoIcon />
                  : <>
                      <DiamondFillIcon color={premiumColor} ratio={0.25} /> Premium
                    </>}
              </Typography>
            : null}
        </div>
      )}
      <NamedStepsNavCTAs
        store={store}
        isPremium={isPremium}
        currentRoute={currentRoute}
        handleNextClick={() => preClickHandler(currentRoute.nextRoute, 'next')}
        premiumPreview={premiumPreview}
        onSave={onSave}
        eventId={eventId}
        setIsLoading={setIsLoading}
        showUpsellModal={showUpsellModal}
        handleModals={modalHandlers}
      />
      <NamedStepsNav
        isPremium={isPremium}
        stepsToShow={stepsToShow}
        filteredEventSteps={filteredEventSteps}
        unlockedSteps={unlockedSteps}
        preClickHandler={preClickHandler}
        handleUnlockStep={handleUnlockStep}
        currentRoute={currentRoute}
        setIsLoading={setIsLoading}
      />
      {renderLoadingPortal()}
      {ErrorModal}
    </nav>
  );
};
